import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  Search, Edit3, X, Check, Filter, Clock, AlertTriangle,
} from "lucide-react";

const RANGE_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
];

const ProductionHistory = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "Main Admin";
  const [days, setDays] = useState(14);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null); // record being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/production-history?days=${days}`);
      setRecords(r.data.records || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load production history");
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter
    ? records.filter((r) => {
        const q = filter.toLowerCase();
        return (
          (r.job_id || "").toLowerCase().includes(q) ||
          (r.operator_name || "").toLowerCase().includes(q) ||
          (r.machine_code || "").toLowerCase().includes(q) ||
          (r.tool_no || "").toLowerCase().includes(q) ||
          (r.part_name || "").toLowerCase().includes(q) ||
          (r.full_batch_code || "").toLowerCase().includes(q) ||
          (r.date || "").includes(q)
        );
      })
    : records;

  const totalOk = filtered.reduce((s, r) => s + (r.ok_qty || 0), 0);
  const totalNg = filtered.reduce((s, r) => s + (r.ng_qty || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Production History" showBack backPath="/home" />
      <div className="max-w-7xl mx-auto p-3 lg:p-6">
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-extrabold text-slate-900 text-xl">Production History</h1>
            <p className="text-xs text-slate-400">उत्पादन इतिहास — {isAdmin ? "View & edit" : "View only"}</p>
          </div>
          {!isAdmin && (
            <span className="inline-flex items-center gap-1.5 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-lg font-bold">
              <AlertTriangle size={12} /> Read-only — only Main Admin can edit
            </span>
          )}
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-3 text-slate-400" />
            <input
              data-testid="prod-history-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search job, operator, machine, tool, part, batch…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3560]/30"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-400" />
            <select
              data-testid="prod-history-range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#1A3560]/30"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="bg-slate-100 px-2.5 py-1.5 rounded-lg font-bold text-slate-700">
              {filtered.length} jobs
            </span>
            <span className="bg-emerald-100 px-2.5 py-1.5 rounded-lg font-bold text-emerald-700">
              {totalOk.toLocaleString()} OK
            </span>
            <span className="bg-red-100 px-2.5 py-1.5 rounded-lg font-bold text-red-700">
              {totalNg.toLocaleString()} NG
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center">
              <div className="w-8 h-8 border-2 border-[#1A3560] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-slate-400">Loading production history…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <Clock size={28} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">No production records in this range</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                  <tr>
                    <Th>Date / Shift</Th>
                    <Th>Job ID</Th>
                    <Th>Machine</Th>
                    <Th>Tool / Part</Th>
                    <Th>Operator</Th>
                    <Th>Batch</Th>
                    <Th align="right">Target</Th>
                    <Th align="right">OK</Th>
                    <Th align="right">NG</Th>
                    <Th align="right">Eff%</Th>
                    <Th>Status</Th>
                    {isAdmin && <Th align="center">Edit</Th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.job_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`prod-row-${r.job_id}`}>
                      <td className="px-3 py-2">
                        <p className="font-bold text-slate-800">{r.date}</p>
                        <p className="text-[10px] text-slate-400">{r.shift || "—"}</p>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{r.job_id}</td>
                      <td className="px-3 py-2 font-bold text-slate-800">{r.machine_code || "—"}</td>
                      <td className="px-3 py-2">
                        <p className="font-bold text-slate-800">T{r.tool_no}</p>
                        <p className="text-[10px] text-slate-400 truncate max-w-[180px]">{r.part_name}</p>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{r.operator_name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.full_batch_code || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{r.target_per_day || 0}</td>
                      <td className="px-3 py-2 text-right font-bold text-emerald-700">{r.ok_qty}</td>
                      <td className={`px-3 py-2 text-right font-bold ${r.ng_qty > 0 ? "text-red-600" : "text-slate-400"}`}>{r.ng_qty}</td>
                      <td className={`px-3 py-2 text-right font-bold ${r.efficiency >= 85 ? "text-emerald-700" : r.efficiency >= 70 ? "text-amber-700" : "text-red-600"}`}>
                        {r.efficiency || 0}%
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${
                          r.status === "COMPLETE" ? "bg-emerald-100 text-emerald-700"
                          : r.status === "OPEN" ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                        }`}>
                          {r.status || "—"}
                        </span>
                        {r.imported && <span className="ml-1 text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded">hist</span>}
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-2 text-center">
                          <button
                            data-testid={`edit-prod-${r.job_id}`}
                            onClick={() => setEditing(r)}
                            className="bg-[#1A3560] text-white px-2.5 py-1 rounded text-[10px] font-bold hover:bg-[#102340] active:scale-95 transition-all inline-flex items-center gap-1"
                          >
                            <Edit3 size={10} /> Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditProductionModal
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const Th = ({ children, align = "left" }) => (
  <th className={`px-3 py-2 text-${align} text-[10px] font-bold uppercase tracking-widest`}>{children}</th>
);

const FIELDS = [
  { key: "date", label: "Date", type: "date" },
  { key: "shift", label: "Shift", type: "text" },
  { key: "machine_code", label: "Machine Code", type: "text" },
  { key: "tool_no", label: "Tool No", type: "text" },
  { key: "part_name", label: "Part Name", type: "text" },
  { key: "operator_name", label: "Operator Name", type: "text" },
  { key: "operator_id", label: "Operator ID", type: "text" },
  { key: "full_batch_code", label: "Batch Code", type: "text" },
  { key: "target_per_day", label: "Target / day", type: "number" },
  { key: "ok_qty", label: "OK Qty", type: "number" },
  { key: "ng_qty", label: "NG Qty", type: "number" },
  { key: "strokes", label: "Strokes", type: "number" },
];

const EditProductionModal = ({ record, onClose, onSaved }) => {
  const [form, setForm] = useState(() =>
    Object.fromEntries(FIELDS.map((f) => [f.key, record[f.key] ?? ""]))
  );
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Only send changed fields
      const payload = {};
      FIELDS.forEach((f) => {
        const cur = form[f.key];
        const orig = record[f.key] ?? "";
        if (String(cur) !== String(orig)) {
          payload[f.key] = f.type === "number" ? Number(cur) : cur;
        }
      });
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }
      await api.patch(`/production-history/${record.job_id}`, payload);
      toast.success(`Updated ${Object.keys(payload).length} field(s) on ${record.job_id}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="edit-prod-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between rounded-t-2xl">
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Edit Production</p>
            <p className="font-extrabold text-slate-900 font-mono">{record.job_id}</p>
          </div>
          <button
            data-testid="close-edit-modal"
            onClick={onClose}
            className="bg-slate-100 hover:bg-slate-200 rounded-full p-2 transition-all"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">
                {f.label}
              </label>
              <input
                data-testid={`edit-field-${f.key}`}
                type={f.type}
                value={form[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1A3560]/30"
              />
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 px-5 py-3 bg-slate-50 rounded-b-2xl flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            data-testid="save-edit-btn"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm font-bold bg-[#1A3560] text-white rounded-lg hover:bg-[#102340] active:scale-95 transition-all disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Check size={14} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductionHistory;
